/**
 * Whether this tab arrived through a password-recovery or invite link.
 *
 * /auth/reset-password is outside GuestGuard (the emailed link signs the
 * person in, and that session is what authorises the change), so it must not
 * offer "set a new password" to any signed-in session: someone at an unlocked
 * laptop could open it and take the account without knowing the password.
 * The form shows only when Supabase reported PASSWORD_RECOVERY, or the URL
 * carried `type=recovery` / `type=invite` (the invite email lands there too).
 *
 * Read at module load, before the Supabase client strips the hash (this file
 * is imported first by shared/lib/supabase.ts). Kept in sessionStorage so a
 * reload of the reset page in the same tab still works; cleared once the
 * password is set and on sign-out.
 */

const STORAGE_KEY = 'unipostudio.passwordRecovery';

const LINK_TYPES: ReadonlySet<string> = new Set(['recovery', 'invite']);

function readLinkType(): string | null {
  if (typeof window === 'undefined') return null;
  const { hash, search } = window.location;
  for (const raw of [hash.replace(/^#/, ''), search.replace(/^\?/, '')]) {
    if (!raw.includes('type=')) continue;
    const type = new URLSearchParams(raw).get('type');
    if (type && LINK_TYPES.has(type)) return type;
  }
  return null;
}

function store(value: string | null): void {
  try {
    if (value) sessionStorage.setItem(STORAGE_KEY, value);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable: the in-memory flag below still covers this load.
  }
}

let recovery: string | null = (() => {
  const fromUrl = readLinkType();
  if (fromUrl) {
    store(fromUrl);
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
})();

/** Supabase emitted PASSWORD_RECOVERY (the PKCE recovery exchange). */
export function markPasswordRecovery(): void {
  recovery = 'recovery';
  store(recovery);
}

/** True when this tab came from a recovery or invite link. */
export function isPasswordRecovery(): boolean {
  return recovery !== null;
}

/** The password was set, or the person signed out. */
export function clearPasswordRecovery(): void {
  recovery = null;
  store(null);
}
